import { IImportVO } from 'library/ts/interfaces/importer/IImportVO';
import { TrueType } from 'library/ts/utilities/modules/enums/TrueType';
import { Utilities } from 'library/ts/utilities/Utilities';

/**
 * @description Promise based JavaScript + CSS loader class
 */
export class Loader {

	/**
	 * @description Load CSS
	 * @param {IImportVO} importVO
	 * @param {boolean} rejectEnabled
	 * @param {Utilities} utils
	 * @return {Promise<any>}
	 */
	static css( importVO: IImportVO, rejectEnabled: boolean = true, utils: Utilities ): Promise<any> {
		return new Promise(
			( resolve, reject ) => {
				Loader._load( 'link', importVO, resolve, reject, rejectEnabled, utils );
			}
		);
	}

	/**
	 * @description Load JavaScript
	 * @param {IImportVO} importVO
	 * @param {boolean} rejectEnabled
	 * @param {Utilities} utils
	 * @return {Promise<any>}
	 */
	static js( importVO: IImportVO, rejectEnabled: boolean = true, utils: Utilities ): Promise<any> {
		return new Promise(
			( resolve, reject ) => {
				Loader._load( 'script', importVO, resolve, reject, rejectEnabled, utils );
			}
		);
	}

	static _load( importTag, importVO: IImportVO, resolve, reject, rejectEnabled: boolean = true, utils: Utilities ) {
		const element: HTMLScriptElement | HTMLStyleElement | any = document.createElement( importTag );
		let sourceAttribute: string;
		let parent: string;

		// check if import is already present
		switch ( utils.Import.alreadyPresent( importVO.url, importVO.namespace ) ) {

			case true: // import is present

				resolve( importVO );

				break;

			case false: // import is not present

				// resolve and reject for the promise
				element.addEventListener(
					'load',
					( event ) => {
						importVO.failed = false;
						importVO.event = event;
						resolve( importVO );
					}
				);
				element.addEventListener(
					'error',
					( event ) => {
						importVO.failed = true;
						importVO.event = event;
						// filter by enabled reject flag
						switch ( rejectEnabled ) {
							case true: // reject enabled
								reject( importVO );
								break;
							case false: // reject disabled
								resolve( importVO, 'failed' );
								break;
						}
					}
				);

				// set different attributes depending on tag type
				switch ( importTag ) {
					case 'script':
						parent = 'body';
						sourceAttribute = 'src';
						element.async = false;
						break;
					case 'link':
						parent = 'head';
						sourceAttribute = 'href';
						element.type = 'text/css';
						element.rel = 'stylesheet';
						break;
					default:
						throw new Error( 'Unsupported tag.' );
				}

				// set the url for the element
				element[ sourceAttribute ] = importVO.url;

				if ( typeof importVO?.attributes === TrueType.OBJECT ) {
					Object.keys( importVO.attributes ).forEach(
						( key ) => {
							// set attribute on script element
							element.setAttribute( key, importVO.attributes[ key ] );
						}
					);
				}

				// initiate the loading of the element
				document[ parent ].appendChild( element );

				break;
		}

	}
}